import React, { useEffect, useState } from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { Target, Users, TrendingUp } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { fetchGoalsRadar } from '../services/api';

/**
 * Radar chart shown at the top of the Goals page.
 * Three overlaid series:
 *   • You      — member's latest per-category score
 *   • Optimal  — optimal benchmark for an optimised longevity profile
 *   • Average  — live community average across all members
 */
export default function GoalsRadar() {
  const [data, setData] = useState(null);
  const [memberCount, setMemberCount] = useState(0);
  const [hasScores, setHasScores] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGoalsRadar()
      .then((r) => {
        setData(r.data || []);
        setMemberCount(r.member_count || 0);
        setHasScores(!!r.has_scores);
      })
      .catch((e) => setError(e?.message || 'Failed to load radar'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="goals-radar-loading" style={{ backgroundColor: COLORS.surface, borderRadius: 18, padding: 24, marginBottom: 16, textAlign: 'center', color: COLORS.textSecondary, fontSize: 14 }}>
        Loading your radar…
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="goals-radar-card"
      style={{
        backgroundColor: COLORS.surface,
        borderRadius: 18,
        padding: '18px 18px 8px',
        marginBottom: 18,
        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.5, color: COLORS.primary, textTransform: 'uppercase' }}>SHIELDED Radar</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, margin: '4px 0 0', fontFamily: 'Outfit, sans-serif' }}>
            You vs. Optimal vs. Community
          </h2>
          <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '4px 0 0', maxWidth: 480, lineHeight: 1.5 }}>
            {hasScores
              ? <>Where your scores sit against the optimised target and the live community average{memberCount > 0 ? <> (n={memberCount.toLocaleString()})</> : null}.</>
              : <>Complete your health survey to populate the “You” series. Optimal and community averages are shown for reference.</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, fontSize: 11, color: COLORS.textSecondary }}>
          <Legend2 color="#6B8E23" icon={TrendingUp} label="You" />
          <Legend2 color="#DAA520" icon={Target} label="Optimal" />
          <Legend2 color="#4682B4" icon={Users} label="Average" />
        </div>
      </div>

      <div style={{ width: '100%', height: 320, marginTop: 4 }}>
        <ResponsiveContainer>
          <RadarChart data={data} outerRadius="76%">
            <PolarGrid stroke={`${COLORS.border}`} />
            <PolarAngleAxis
              dataKey="category"
              tick={{ fill: COLORS.textPrimary, fontSize: 11, fontWeight: 600 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{ fill: COLORS.textSecondary, fontSize: 10 }}
              axisLine={false}
            />
            <Radar name="Optimal" dataKey="optimal" stroke="#DAA520" fill="#DAA520" fillOpacity={0.10} strokeWidth={2} />
            <Radar name="Average" dataKey="average" stroke="#4682B4" fill="#4682B4" fillOpacity={0.12} strokeWidth={2} />
            <Radar name="You" dataKey="you" stroke="#6B8E23" fill="#6B8E23" fillOpacity={0.35} strokeWidth={2.4} />
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              contentStyle={{
                backgroundColor: '#2F3E46',
                border: 'none',
                borderRadius: 10,
                color: '#FFF',
                fontSize: 12,
                padding: '8px 12px',
              }}
              labelStyle={{ color: '#FFF', fontWeight: 700, marginBottom: 4 }}
              itemStyle={{ color: '#FFF' }}
              formatter={(value, name) => [`${Math.round(value)}`, name]}
            />
            <Legend
              align="center"
              verticalAlign="bottom"
              wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
              iconSize={10}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Legend2({ color, icon: Icon, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 9999, backgroundColor: `${color}15`, color }}>
      <Icon size={12} />
      <span style={{ fontWeight: 700 }}>{label}</span>
    </span>
  );
}
